import {
  Alliance,
  ArmyRegiment,
  Crime,
  General,
  GeneralQuality,
  NewsData,
  Slot,
  Spell,
  ThiefActionType
} from "./types";
import axiosGovernorate from "../axios-governorate";
import { CONTENT_CONTEXT, MONTH_NAMES } from "./enums";

export const isGovernorateEmperor = (
  governorateId: number,
  alliance: Alliance
) => alliance.emperor.id === governorateId;

export const getCrimeType = (crime: Crime, crimeTypes: ThiefActionType[]) => crimeTypes
  .find(type => type.id === crime.typeId);

export const getData = async (url: string) => {
  const response = await axiosGovernorate.get(url);

  const { data } = JSON.parse(response.data);
  return data;
}

export const checkDuplicity = async (name: string, context: CONTENT_CONTEXT) => {
  const response = await axiosGovernorate.get(`/api/duplicity/${context}/${name}`);

  const data = JSON.parse(response.data);
  return !!Number.parseInt(data, 10);
}

export const formatNumber = (
  number: number,
  forceSigned?: boolean
) => (number !== 0
  ? `${number > 0 && forceSigned ? "+" : ""}${new Intl.NumberFormat('cs-CS').format(number)}`
  : "0");

export const formatNumberForceSigned = (number: number) => formatNumber(number, true);

export const formatDate = (date: Date) => date.toLocaleString();

export const formatToPostDate = (date: Date) => `${MONTH_NAMES[date.getMonth() + 1]} ${date.getDate()}, ${date.toLocaleTimeString(
  [],
  {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit"
  }
)}`;

export const getLearntSpell = (
  learntSpells: number[],
  spells: Spell[],
  family: number
) => {
  const familyIds = spells.filter(s => s.family === family).map(s => s.id);

  return learntSpells.find(id => familyIds.includes(id));
}

export const isSpellPositive = (spellId: number, spells: Spell[]) => {
  const spell = spells.find(s => s.id === spellId);

  return spell && spell.buff;
}

export const isSpellNegative = (spellId: number, spells: Spell[]) => {
  const spell = spells.find(s => s.id === spellId);

  return spell && (spell.debuff || spell.damage);
}

export const isSpellCast = (spells: {
  id: number;
  tableId: number;
  level: number;
  targetId: number;
}[], spellId: number) => spells.some(s => s.id === spellId);

export const getSlotsByType = (slotTypeId: number, slots: Slot[]) => slots
  .reduce<Slot[]>((filteredSlots: Slot[], slot: Slot) => (slot.activeType.id === slotTypeId
    ? [...filteredSlots, slot]
    : filteredSlots), []);

export const getSlotsByChain = (chainId: number, slots: Slot[]) => slots
  .reduce<Slot[]>((filteredSlots: Slot[], slot: Slot) => (slot.activeType.chain === chainId
    ? [...filteredSlots, slot]
    : filteredSlots), []);

export const getActiveSlotType = (slot: Slot) => (slot.turnsToUpgrade > 0
  ? slot.lowerLevelType : slot.type);

export const isUltimateTierAvailable = (general: General) => general.skills.reduce(
  (ultimatesLevel: number, skill) => ultimatesLevel += (skill.tier === 4 ? skill.level : 0),
  0
) < Math.floor(general.skills.reduce(
  (skillsLevel: number, skill) => skillsLevel += (skill.tier !== 4 ? skill.level : 0),
  0
) / 4);

export const getGeneralSkill = (
  general: General,
  skillId: number
) => general.skills.find(skill => skill.id === skillId);

export const hasGeneralSkill = (general: General, skillId: number) => {
  const skill = getGeneralSkill(general, skillId);

  return !!skill && !!skill.level;
};

export const getGeneralSkillLevel = (general: General, skillId: number) => {
  const skill = getGeneralSkill(general, skillId);

  return skill ? skill.level : 0;
}

export const getGeneralSkillName = (general: General, skillId: number) => {
  const skill = general.skills.find(s => s.id === skillId);
  return skill ? skill.name : undefined;
}

export const isExclusiveUltimateActive = (
  general: General,
  skill: GeneralQuality
) => general.skills.some(s => s.id === skill.exclusive && s.level);

export const getNewsDetailValue = (
  news: NewsData,
  detailId: number
) => (news.details[detailId] ? news.details[detailId].value : "");

export const getTenth = (intNumber: number) => {
  if (intNumber < 10) {
    return 0;
  }

  return (intNumber % 10 && Math.floor(intNumber / 10) * 10) || intNumber;
}

export const isTacticsAvailable = (tactics: any, banners: any) => Object.keys(banners)
  // @ts-ignore
  .every((b: any) => !banners[b]?.regiments || tactics[b] >= Object.values(banners[b].regiments)
    // @ts-ignore
    .reduce((units: number, r: ArmyRegiment) => units += Object.values(r.units)
      .reduce((sum, u) => sum += u.amount, 0), 0));
